import React, { useEffect, useState } from 'react';

const WidgetPreviewDisplay = () => {
    const [backgroundImage, setBackgroundImage] = useState(null);

    useEffect(() => {
        // Get background image from sessionStorage
        const storedImage = sessionStorage.getItem('widget_preview_background');
        if (storedImage) {
            setBackgroundImage(storedImage);

            const img = new Image();
            img.onload = () => {
                console.log(`Preview image loaded: ${img.naturalWidth}x${img.naturalHeight}`);
            };
            img.src = storedImage;
        }

        // Pre-inject CSS to ensure styles are ready before widget HTML is created
        if (!document.getElementById('chat-widget-stylesheet')) {
            const cssLink = document.createElement('link');
            cssLink.id = 'chat-widget-stylesheet';
            cssLink.rel = 'stylesheet';
            cssLink.href = '/widget.css';
            document.head.appendChild(cssLink);
        }

        // Load widget script
        const loadWidget = () => {
            const script = document.createElement('script');
            script.src = '/widget.js';
            script.async = true;

            script.onload = () => {
                console.log('Widget script loaded');

                setTimeout(() => {
                    if (window.ChatWidget) {
                        const backendUrl = process.env.REACT_APP_API_BASE_URL?.replace('/api', '');
                        const activeAiAgentId = localStorage.getItem('activeAiAgentId') || 'b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03';

                        const syncedConfigJson = sessionStorage.getItem('widget_preview_config');
                        const syncedConfig = syncedConfigJson ? JSON.parse(syncedConfigJson) : {};

                        const authToken = (() => {
                            try { return JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || '{}').token; } catch { return undefined; }
                        })();

                        window.ChatWidget.init({
                            apiUrl: backendUrl,
                            aiAgentId: activeAiAgentId,
                            ...syncedConfig,
                            isPreview: true,
                            authToken
                        });

                        console.log(`Widget initialized for agent ${activeAiAgentId}`);
                    } else {
                        console.error('ChatWidget not found on window object');
                    }
                }, 500);
            };

            script.onerror = () => {
                console.error('Failed to load widget script');
            };

            document.body.appendChild(script);
        };

        loadWidget();

        return () => {
            const widgetContainer = document.querySelector('.chat-widget-container');
            if (widgetContainer) {
                widgetContainer.remove();
            }
        };
    }, []);

    if (!backgroundImage) return <EasyChatDemoPage />;

    return (
        <div
            className="w-screen h-screen m-0 p-0 overflow-hidden relative"
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
            }}
        />
    );
};

// Sample website shown behind the widget when no preview image was uploaded, so the widget is seen in context
const DEMO_FEATURES = [
    { title: 'AI agents', text: 'Answers from your documents, website and tools, around the clock.' },
    { title: 'Live chat', text: 'Hands the conversation to your team when a person is needed.' },
    { title: 'Every channel', text: 'Website, WhatsApp, Telegram, Slack and email in one inbox.' },
    { title: 'Analytics', text: 'Resolution, sentiment, tokens and cost for every conversation.' },
];

const EasyChatDemoPage = () => (
    <div className="min-h-screen w-full bg-white text-zinc-900" style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
        <header className="border-b border-zinc-100">
            <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
                <img src="/easychat-logo.png" alt="EasyChat" className="h-7 w-auto" onError={(e) => { e.currentTarget.replaceWith(document.createTextNode('EasyChat')); }} />
                <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-500">
                    <span>Product</span><span>Integrations</span><span>Pricing</span><span>Contact</span>
                </nav>
                <span className="text-sm font-medium px-4 py-2 rounded-lg bg-zinc-900 text-white">Get started</span>
            </div>
        </header>

        <main className="max-w-6xl mx-auto px-6">
            <section className="py-16 md:py-24 max-w-2xl">
                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">Widget preview</span>
                <h1 className="mt-5 text-4xl md:text-5xl font-semibold leading-tight tracking-tight">
                    Turn every conversation into an opportunity
                </h1>
                <p className="mt-5 text-lg text-zinc-500 leading-relaxed">
                    This is a sample page so you can see your chat widget the way visitors will. Open it from the
                    corner and try a real conversation with your AI agent.
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                    <span className="text-sm font-medium px-5 py-3 rounded-lg bg-zinc-900 text-white">Book a demo</span>
                    <span className="text-sm font-medium px-5 py-3 rounded-lg border border-zinc-200 text-zinc-700">See how it works</span>
                </div>
            </section>

            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 pb-20">
                {DEMO_FEATURES.map((f) => (
                    <div key={f.title} className="rounded-xl border border-zinc-100 bg-zinc-50/60 p-5">
                        <h2 className="text-sm font-semibold text-zinc-900">{f.title}</h2>
                        <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{f.text}</p>
                    </div>
                ))}
            </section>

            <p className="pb-10 text-xs text-zinc-400">
                Tip: upload a screenshot of your own website on the Widget Preview page to preview the widget on it.
            </p>
        </main>
    </div>
);

export default WidgetPreviewDisplay;
